import { useCallback, useEffect, useRef, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { useLazyGetStorageLocationsQuery } from '@/app/services/locations';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { SortEnumType, StorageLocation } from '@/rtk-query.gql';

import './StorageLocationAutoComplete.css';

type StorageLocationAutoCompleteProps = Omit<
  AutoCompleteProps,
  'defaultValue'
> & {
  className?: string;
  label?: string;
  defaultValue?: StorageLocation;
};

export const StorageLocationAutoComplete = ({
  className,
  label,
  defaultValue,
  onSelect,
  onChange: externalOnChange,
  ...autoCompleteProps
}: StorageLocationAutoCompleteProps) => {
  const [selected, setSelected] = useState<StorageLocation | undefined>(
    defaultValue,
  );
  const [filtered, setFiltered] = useState<StorageLocation[]>([]);
  const [inputValue, setInputValue] = useState(defaultValue?.name);
  const userChangedRef = useRef(false);

  const [query] = useLazyGetStorageLocationsQuery();

  useEffect(() => {
    if (userChangedRef.current) {
      userChangedRef.current = false;
      return;
    }
    setSelected(defaultValue);
    setInputValue(defaultValue?.name || '');
  }, [defaultValue]);

  const search = useCallback(
    async (event: { query: string }) => {
      const where = event.query ? { name: { contains: event.query } } : {};

      const { data = [] } = await query({
        skip: 0,
        take: SELECTION_LIMIT,
        order: [{ name: SortEnumType.ASC }],
        where,
      });

      setFiltered(data);
    },
    [query],
  );

  const handleSelect = (e: { value: StorageLocation }) => {
    userChangedRef.current = true;
    setSelected(e.value);
    setInputValue(e.value?.name as string);
    if (onSelect) {
      onSelect(e as any);
    }
  };

  const handleChange = (e: { value: string }) => {
    userChangedRef.current = true;
    setInputValue(e.value as string);
    externalOnChange?.(e as any);
  };
  return (
    <div
      className={`[ storage-location-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo="self"
        suggestions={filtered}
        field="name"
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
      />
    </div>
  );
};
